import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { BarChart3, ArrowLeft, TrendingUp, TrendingDown } from 'lucide-react'

export default function BandwidthMonitorPage() {
  const navigate = useNavigate()
  const [monthData] = useState([
    { month: 'January', used: 1800, limit: 2400, inbound: 950, outbound: 850 },
    { month: 'February', used: 1950, limit: 2400, inbound: 1050, outbound: 900 },
    { month: 'March', used: 2100, limit: 2400, inbound: 1150, outbound: 950 },
    { month: 'April', used: 2250, limit: 2400, inbound: 1200, outbound: 1050 },
    { month: 'May', used: 2400, limit: 2400, inbound: 1280, outbound: 1120 },
    { month: 'Current', used: 850, limit: 2400, inbound: 480, outbound: 370 },
  ])

  const stats = useMemo(() => {
    const current = monthData[monthData.length - 1]
    return {
      currentUsage: current.used,
      monthlyLimit: current.limit,
      percentUsed: Math.round((current.used / current.limit) * 100),
      inbound: current.inbound,
      outbound: current.outbound
    }
  }, [monthData])

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="max-w-7xl mx-auto">
        <button onClick={() => navigate('/network')} className="flex items-center gap-2 text-blue-400 hover:text-blue-300 mb-6 font-semibold">
          <ArrowLeft size={20} /> Back to Network Management
        </button>

        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Bandwidth Monitor</h1>
          <p className="text-slate-400">Track bandwidth usage and optimize network performance</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Current Usage</div>
            <div className="text-3xl font-bold text-blue-400">{stats.currentUsage} GB</div>
            <p className="text-slate-400 text-xs mt-2">of {stats.monthlyLimit} GB</p>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Usage Percentage</div>
            <div className="text-3xl font-bold text-yellow-400">{stats.percentUsed}%</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Inbound</div>
            <div className="text-3xl font-bold text-green-400">{stats.inbound} GB</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Outbound</div>
            <div className="text-3xl font-bold text-cyan-400">{stats.outbound} GB</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <h3 className="text-lg font-bold text-white mb-6">Monthly Usage Trend</h3>
            <div className="space-y-4">
              {monthData.map((data, idx) => (
                <div key={idx}>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-400">{data.month}</span>
                    <span className="text-white font-semibold">{data.used}/{data.limit} GB</span>
                  </div>
                  <div className="w-full h-3 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full bg-gradient-to-r ${data.used >= data.limit ? 'from-red-500 to-red-600' : 'from-blue-500 to-cyan-500'}`}
                      style={{ width: `${(data.used / data.limit) * 100}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <h3 className="text-lg font-bold text-white mb-6">Traffic Distribution</h3>
            <div className="space-y-4">
              <div className="bg-slate-700/30 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-2">
                    <TrendingDown size={18} className="text-green-400" />
                    <span className="text-white font-semibold">Inbound Traffic</span>
                  </div>
                  <span className="text-green-400 font-bold">{stats.inbound} GB</span>
                </div>
                <p className="text-slate-400 text-sm">{Math.round((stats.inbound / stats.currentUsage) * 100)}% of total</p>
              </div>
              <div className="bg-slate-700/30 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-2">
                    <TrendingUp size={18} className="text-cyan-400" />
                    <span className="text-white font-semibold">Outbound Traffic</span>
                  </div>
                  <span className="text-cyan-400 font-bold">{stats.outbound} GB</span>
                </div>
                <p className="text-slate-400 text-sm">{Math.round((stats.outbound / stats.currentUsage) * 100)}% of total</p>
              </div>
              <div className="bg-blue-600/10 border border-blue-500/30 rounded-lg p-4">
                <p className="text-blue-300 text-sm">
                  <strong>Recommendation:</strong> Current bandwidth usage is within acceptable limits. Monitor for peaks during business hours.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
          <h3 className="text-lg font-bold text-white mb-4">Optimization Tips</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-slate-700/30 rounded p-4">
              <p className="text-white font-semibold mb-2">QoS Configuration</p>
              <p className="text-slate-400 text-sm">Implement Quality of Service rules to prioritize critical traffic</p>
            </div>
            <div className="bg-slate-700/30 rounded p-4">
              <p className="text-white font-semibold mb-2">Traffic Compression</p>
              <p className="text-slate-400 text-sm">Enable compression for HTTP traffic to reduce bandwidth</p>
            </div>
            <div className="bg-slate-700/30 rounded p-4">
              <p className="text-white font-semibold mb-2">Caching Policies</p>
              <p className="text-slate-400 text-sm">Implement intelligent caching for frequently accessed content</p>
            </div>
            <div className="bg-slate-700/30 rounded p-4">
              <p className="text-white font-semibold mb-2">Usage Monitoring</p>
              <p className="text-slate-400 text-sm">Set alerts for unusual spikes in bandwidth consumption</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
